import { TextEllipsis } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { Grid, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import { useBrowseTaxIdApi } from '../../api/use-browse-tax-id-api';
import { useReadCustomerApi } from '../../api/use-read-customer-api';
import { BillingAddressHeader } from './components/billing-address-header';
import * as Styled from './styles';
import * as SX from './sx';
import type { BillingAddressProps as Props } from './types';

export const BillingAddress = memo(({ className }: Props) => {
  const workspaceId = useWorkspace()?.id;
  const { data } = useReadCustomerApi(workspaceId || '');
  const { data: taxIds = [] } = useBrowseTaxIdApi();

  return (
    <Styled.Wrapper
      className={classnames('BillingAddress', className)}
      sx={SX.root}
    >
      <BillingAddressHeader />
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <Stack gap={1}>
            <Stack>
              <TextEllipsis
                variant="body4"
                fontWeight={500}
                color="grey.600"
                width="150px"
              >
                {data?.name || '-'}
              </TextEllipsis>
              <Typography variant="body5" fontWeight={400} color="grey.600">
                Company Name
              </Typography>
            </Stack>
            <Stack>
              <TextEllipsis
                variant="body4"
                fontWeight={500}
                color="grey.600"
                width="150px"
              >
                {data?.address?.line1 || '-'}
              </TextEllipsis>
              <Typography variant="body5" fontWeight={400} color="grey.600">
                Address Line 1
              </Typography>
            </Stack>
            <Stack>
              <TextEllipsis
                variant="body4"
                fontWeight={500}
                color="grey.600"
                width="150px"
              >
                {data?.address?.line2 || '-'}
              </TextEllipsis>
              <Typography variant="body5" fontWeight={400} color="grey.600">
                Address Line 2
              </Typography>
            </Stack>
            <Stack>
              <TextEllipsis
                variant="body4"
                fontWeight={500}
                color="grey.600"
                width="150px"
              >
                {data?.address?.postalCode || '-'}
              </TextEllipsis>
              <Typography variant="body5" fontWeight={400} color="grey.600">
                Postal Code
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack gap={1}>
            <Stack>
              <TextEllipsis
                variant="body4"
                fontWeight={500}
                color="grey.600"
                width="150px"
              >
                {data?.address?.city || '-'}
              </TextEllipsis>
              <Typography variant="body5" fontWeight={400} color="grey.600">
                City
              </Typography>
            </Stack>
            <Stack>
              <TextEllipsis
                variant="body4"
                fontWeight={500}
                color="grey.600"
                width="150px"
              >
                {data?.address?.country || '-'}
              </TextEllipsis>
              <Typography variant="body5" fontWeight={400} color="grey.600">
                Country
              </Typography>
            </Stack>
            <Stack>
              <TextEllipsis
                variant="body4"
                fontWeight={500}
                color="grey.600"
                width="150px"
              >
                {taxIds?.[0]?.value || '-'}
              </TextEllipsis>
              <Typography variant="body5" fontWeight={400} color="grey.600">
                VAT ID
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Styled.Wrapper>
  );
});

BillingAddress.displayName = 'BillingAddress';
